import { RootState } from '@/store/store';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * The overlay appears only when a request has been in flight this long. Most calls (locks,
 * pollers, small saves) finish well inside it, so they no longer flash a dark frame over the app.
 */
export const LOADER_DELAY_MS = 400;
/** Once shown, the overlay stays at least this long so it never blinks. */
export const LOADER_MIN_VISIBLE_MS = 300;

const loaderStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 100,
  backdropFilter: 'blur(3px)',
  animation: 'loader-fade-in 160ms ease-out both',
};

const spinnerContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
};

const spinnerStyle: React.CSSProperties = {
  width: '60px',
  height: '60px',
  border: '5px solid rgba(255, 255, 255, 0.2)',
  borderTopColor: '#4f46e5',
  borderRightColor: '#4f46e5',
  borderRadius: '50%',
  animation: 'spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
};

const dotPulseStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const dotStyle = (delay: string): React.CSSProperties => ({
  width: '12px',
  height: '12px',
  backgroundColor: '#4f46e5',
  borderRadius: '50%',
  animation: `pulse 1.4s ease-in-out ${delay} infinite`,
  opacity: 0.6,
});

const textStyle: React.CSSProperties = {
  color: 'white',
  fontSize: '1rem',
  fontWeight: 500,
  letterSpacing: '0.5px',
};

/**
 * Turns the raw in-flight flag into a calm visible state: delayed on, held on.
 * The flag itself is untouched, so screens that set it by hand keep working.
 */
export function useDelayedVisibility(active: boolean, delayMs = LOADER_DELAY_MS, minVisibleMs = LOADER_MIN_VISIBLE_MS): boolean {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (active && !visible) {
      timer = setTimeout(() => { shownAt.current = Date.now(); setVisible(true); }, delayMs);
    } else if (!active && visible) {
      const remaining = Math.max(0, minVisibleMs - (Date.now() - (shownAt.current ?? 0)));
      timer = setTimeout(() => { shownAt.current = null; setVisible(false); }, remaining);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [active, visible, delayMs, minVisibleMs]);

  return visible;
}

export default function GlobalLoader() {
  const isLoading = useSelector((state: RootState) => state.loading.isLoading);
  const visible = useDelayedVisibility(isLoading);

  if (!visible) return null;

  return (
    <div style={loaderStyle} aria-label="Loading..." role="status">
      <div style={spinnerContainerStyle}>
        <div style={spinnerStyle} />
        <div style={dotPulseStyle}>
          <div style={dotStyle('0s')} />
          <div style={dotStyle('0.2s')} />
          <div style={dotStyle('0.4s')} />
        </div>
        <span style={textStyle}>Loading...</span>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes loader-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(0.8);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }
      ` }} />
    </div>
  );
}
